import * as Sentry from "@sentry/react";
import { useAuthAccountStore, useAuthCompanyStore, useCompaniesStore, useLocalOfflineStore, useProductStore, useSalesLastStore, useSyncStore } from "@sicarx/sxw-api"
import { EventWorker, createWorker } from "@sicarx/sxw-core"
import { useCallback, useEffect, useRef } from "react"

import SyncWorker from "../workers/sync.worker?worker&url"
import { type Incoming, type Outgoing, type IncomingData, OutgoingAction, IncomingAction, SyncEvents } from "../workers/types"
import { SyncSaleError } from "../utils/errors"

const PLATFORM = !window?.electron ? 'WEB' : "ELECTRON"

const useSubscriptionSync = () => {

    const syncWorker = useRef<EventWorker<Outgoing, Incoming>>()

    const syncEvent = useSyncStore(store => store.syncEvent)

    const priceListId = useProductStore(store => store.prices.priceList?.uuid!)
    const branchId = useCompaniesStore(store => store.companies.branchOffice.id)
    const { getTokens, setTokens, setCompleted } = useLocalOfflineStore(store => ({
        getTokens: () => (store.getData()),
        setTokens: store.setTokens,
        setCompleted: store.setCompleted,
    }))

    const { contentId, authToken, } = useAuthCompanyStore(store => ({ contentId: store.authCompany?.contentId!, authToken: store.authCompany.jwt }))
    const userAccount = useAuthAccountStore(store => store.authAccount)

    const getIncomingData = useCallback(() => ({
        contentId,
        authToken,
        priceListId,
        platform: PLATFORM,
        ...getTokens()
    } satisfies IncomingData), [getTokens, contentId, authToken, priceListId])

    useEffect(() => {

        if (syncEvent) {

            switch (syncEvent) {
                case SyncEvents.START_CATALOG:
                    syncWorker.current?.send(IncomingAction.INIT_SYNC, getIncomingData())
                    break;
                case SyncEvents.STOP_CATALOG:
                    syncWorker.current?.send(IncomingAction.STOP_SYNC_CATALOG)
                    break;
                case SyncEvents.STOP_SALES:
                    syncWorker.current?.send(IncomingAction.STOP_SYNC_SALES)
                    break;
                case SyncEvents.START_SALES:
                    syncWorker.current?.send(IncomingAction.DOCUMENTS, { contentId, branchId, authToken, userAccount: userAccount.email, platform: PLATFORM, isInmediate: true })
                    break;
            }

            useSyncStore.getState().emit('CLOSE')
        }

    }, [syncEvent])

    useEffect(() => {

        //if (window?.electron || !window?.sicarx?.isWebProduction) {
        if (branchId && priceListId) {

            try {

                syncWorker.current = new EventWorker<Outgoing, Incoming>(createWorker(SyncWorker))

                const localTokens = getTokens()
                const isCompleted = Boolean(localTokens?.isCompleted?.[priceListId])

                //Get Only Prices
                if (!isCompleted && localTokens?.tokens?.PRODUCT && !localTokens?.tokens?.PRICE?.[priceListId]) {

                    syncWorker.current.send(IncomingAction.SYNC_ONLY_PRICES, getIncomingData())

                } else {

                    const canUseCatalog = useLocalOfflineStore.getState().getData()?.canUseCatalog
                    if (canUseCatalog) {
                        syncWorker.current.send(!isCompleted ? IncomingAction.INIT_SYNC : IncomingAction.SUBSCRIBE, getIncomingData())
                    }

                    if (isCompleted) {

                        const canWorks = useLocalOfflineStore.getState().canWorksWithOutConnection()
                        if (canWorks) {
                            syncWorker.current.send(IncomingAction.DOCUMENTS, { contentId, branchId, authToken, userAccount: userAccount.email, platform: PLATFORM })
                        }


                    }
                }

                syncWorker.current.on(OutgoingAction.PANIC, (panic) => {
                    useSyncStore.getState().setPanic(panic)
                })

                syncWorker.current.on(OutgoingAction.SYNC_ERROR, (data) => {

                    Sentry.captureException(
                        new SyncSaleError({
                            message: `Error synchronizing sale ${data.payload.offline?.id}`,
                            [data.isBackError ? 'cause' : 'stack']: data.error
                        }),
                        {
                            level: 'error',
                            contexts: {
                                [data.isBackError ? 'Response' : 'Error']: { data: data.error },
                                ['Payload']: { data: JSON.stringify(data.payload) }
                            }
                        }
                    )

                })

                //Set State
                syncWorker.current.on(OutgoingAction.SET_COMPLETED, (data) => {
                    setCompleted(data)
                })

                syncWorker.current.on(OutgoingAction.SYNC, (data) => {
                    useSyncStore.getState().setSyncActive(data)
                })

                syncWorker.current.on(OutgoingAction.SYNC_SALES, (data) => {
                    useSyncStore.getState().setSyncSalesActive(data)
                })

                syncWorker.current.on(OutgoingAction.SET_LAST_SALE, (sale) => {
                    useSalesLastStore.getState().setLastSale(sale)
                })

                syncWorker.current.on(OutgoingAction.SET_TOKENS, (data) => {
                    setTokens('tokens', data)
                })

                syncWorker.current.on(OutgoingAction.SET_PAGINATION_TOKENS, (data) => {
                    setTokens('paginationTokens', data)
                })

                syncWorker.current.on(OutgoingAction.SUBSCRIBE, () => {
                    setTimeout(() => {
                        syncWorker.current?.send(IncomingAction.SUBSCRIBE, getIncomingData())
                    }, 600)
                })

                syncWorker.current.onerror((error) => console.warn(`SyncWorker::Error`, error))
                syncWorker.current.listen()

                window.ononline = () => {

                    const canUseCatalog = useLocalOfflineStore.getState().getData()?.canUseCatalog
                    if (canUseCatalog) {

                        setTimeout(() => {

                            const isCompleted = getTokens()?.isCompleted?.[priceListId]
                            syncWorker.current?.send(!isCompleted ? IncomingAction.INIT_SYNC : IncomingAction.SUBSCRIBE, getIncomingData())

                        }, 1000)
                    }

                    const canWorks = useLocalOfflineStore.getState().canWorksWithOutConnection()
                    if (canWorks) {
                        setTimeout(() => {
                            syncWorker.current?.send(IncomingAction.DOCUMENTS, { contentId, branchId, authToken, userAccount: userAccount.email, platform: PLATFORM, isInmediate: true })
                        }, 3000)
                    }

                }

            } catch (err) {
                console.warn('Sync::useSubscription', err)
            }
        }

        //}

        return () => {
            syncWorker.current?.terminate()
        }

    }, [branchId, priceListId])
}

export default useSubscriptionSync

